export interface Observation {
  id: number;
  hex: string;
  flight: string | null;
  registration: string | null;
  type_code: string | null;
  observed_at: number;
  lat: number | null;
  lon: number | null;
  alt_baro: number | null;
  ground_speed: number | null;
  track: number | null;
  squawk: string | null;
  alert: boolean;
  spi: boolean;
  on_ground: boolean;
}

export interface ObservationsPage {
  items: Observation[];
  next_cursor: string | null;
  has_more: boolean;
}

export interface TrackPoint {
  observed_at: number;
  lat: number;
  lon: number;
  alt_baro: number | null;
}

export interface AircraftRecord {
  hex: string;
  registration: string | null;
  type_code: string | null;
  description: string | null;
  owner_operator: string | null;
  last_seen_at: number;
}

export interface FlightSegment {
  start_at: number;
  end_at: number;
  callsign: string | null;
  position_count: number;
}

export interface AircraftSummary {
  hex: string;
  registration: string | null;
  type_code: string | null;
  description: string | null;
  owner_operator: string | null;
  manufacture_year: number | null;
  is_military: boolean;
  callsigns: string[];
  squawks: string[];
  alert_ever: boolean;
  spi_ever: boolean;
  first_observed_at: number;
  last_observed_at: number;
  position_count: number;
  on_ground_count: number;
  alt_min: number | null;
  alt_max: number | null;
  gs_max: number | null;
  is_mlat: boolean;
  is_tisb: boolean;
}

export interface ObsFilters {
  start?: number;
  end?: number;
  hex?: string;
  flight?: string;
  registration?: string;
  alt_min?: number;
  alt_max?: number;
  squawk?: string;
  alert?: boolean;
  spi?: boolean;
  military?: boolean;
  on_ground?: boolean;
  lat?: number;
  lon?: number;
  radius_nm?: number;
  cursor?: string;
  limit?: number;
}

function buildParams(filters: ObsFilters): URLSearchParams {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== null && v !== "") {
      p.set(k, String(v));
    }
  }
  return p;
}

export async function listObservations(
  filters: ObsFilters
): Promise<ObservationsPage> {
  const resp = await fetch(`/api/observations?${buildParams(filters)}`);
  if (!resp.ok) throw new Error(`GET /api/observations: ${resp.status}`);
  return resp.json();
}

export async function getTrack(
  hex: string,
  start?: number,
  end?: number,
): Promise<TrackPoint[]> {
  const p = new URLSearchParams();
  if (start !== undefined) p.set("start", String(start));
  if (end !== undefined) p.set("end", String(end));
  const qs = p.toString() ? `?${p}` : "";
  const resp = await fetch(`/api/observations/${encodeURIComponent(hex)}/track${qs}`);
  if (!resp.ok) throw new Error(`GET /api/observations/${hex}/track: ${resp.status}`);
  return resp.json();
}

export async function searchAircraft(q: string): Promise<AircraftRecord[]> {
  const resp = await fetch(`/api/aircraft?q=${encodeURIComponent(q)}`);
  if (!resp.ok) throw new Error(`GET /api/aircraft: ${resp.status}`);
  return resp.json();
}

export async function getFlightSegments(
  hex: string,
  gapMin = 30,
): Promise<FlightSegment[]> {
  const resp = await fetch(
    `/api/observations/${encodeURIComponent(hex)}/flights?gap_min=${gapMin}`,
  );
  if (!resp.ok) throw new Error(`GET /api/observations/${hex}/flights: ${resp.status}`);
  return resp.json();
}

export async function getAircraftSummary(
  hex: string,
  start: number,
  end: number,
): Promise<AircraftSummary> {
  const resp = await fetch(
    `/api/observations/${encodeURIComponent(hex)}/summary?start=${start}&end=${end}`,
  );
  if (!resp.ok) throw new Error(`GET /api/observations/${hex}/summary: ${resp.status}`);
  return resp.json();
}

export function exportUrl(filters: ObsFilters): string {
  const p = buildParams(filters);
  return `/api/export/observations?${p}`;
}
